import { loadWorkspace, saveWorkspace } from '@shared/storage';
import { Workspace } from '@shared/types';
import { SyncEngine, SyncResult, SyncStatus } from './types';

const HUB_SYNC_KEY = 'indxone.hub.v1';

const listeners = new Set<() => void>();

export interface WorkspaceConflict {
  local: Workspace;
  remote: Workspace;
  detectedAt: number;
  localDevice: string;
  remoteDevice: string;
}

let pendingConflict: WorkspaceConflict | null = null;

let currentStatus: SyncStatus = {
  mode: 'local-only',
  phase: 'idle',
  label: 'Local',
  message: 'Mode local actif.',
  source: 'local',
};

function emit() {
  listeners.forEach((listener) => listener());
}

function setStatus(next: SyncStatus) {
  currentStatus = next;
  emit();
}

export function resolveWorkspacePull(local: Workspace, remote: Workspace | null): {
  workspace: Workspace;
  conflict: boolean;
} {
  if (!remote) return { workspace: local, conflict: false };
  if (local.meta.updatedAt !== remote.meta.updatedAt) {
    return { workspace: local, conflict: true };
  }
  return { workspace: remote, conflict: false };
}

function getDeviceLabel(): string {
  if (typeof navigator === 'undefined') return 'Cet appareil';
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return 'iPhone / iPad';
  if (/android/i.test(ua)) return 'Android';
  if (/macintosh|mac os/i.test(ua)) return 'Mac';
  if (/windows/i.test(ua)) return 'Windows';
  return 'Cet appareil';
}

export function getPendingWorkspaceConflict(): WorkspaceConflict | null {
  return pendingConflict;
}

export function keepLocalWorkspace(): void {
  pendingConflict = null;
  const status = buildStatus({
    phase: 'synced',
    message: 'Version locale conservée. La sauvegarde cloud reste disponible.',
    source: 'local',
    lastSyncAt: currentStatus.lastSyncAt,
  });
  setStatus(status);
}

export function restorePendingRemoteWorkspace(): Workspace | null {
  if (!pendingConflict) return null;
  const remote = pendingConflict.remote;
  saveWorkspace(remote);
  pendingConflict = null;
  setStatus(buildStatus({
    phase: 'synced',
    message: 'Version cloud restaurée après confirmation.',
    source: 'cloud',
    lastSyncAt: Date.now(),
  }));
  return remote;
}

function isPro(): boolean {
  const w = window as any;
  if (typeof w.IndxLicense?.isPro === 'function') return w.IndxLicense.isPro();
  if (typeof w.IndxSync?.isAvailable === 'function') return w.IndxSync.isAvailable();
  return false;
}

function getIndxSync(): any | null {
  const w = window as any;
  return w.IndxSync || null;
}

function buildStatus(overrides: Partial<SyncStatus>): SyncStatus {
  return {
    mode: 'cloud-ready',
    phase: 'synced',
    label: 'Cloud',
    message: 'Sauvegarde cloud active.',
    source: 'cloud',
    lastSyncAt: currentStatus.lastSyncAt,
    ...overrides,
  };
}

async function writeWorkspaceToHubKey(workspace: Workspace): Promise<void> {
  try {
    const cloudWorkspace: Workspace = {
      ...workspace,
      meta: { ...workspace.meta, deviceLabel: getDeviceLabel() },
    };
    localStorage.setItem(HUB_SYNC_KEY, JSON.stringify(cloudWorkspace));
  } catch (e) {
    console.error('[CloudSync] Failed to write hub key:', e);
  }
}

async function readWorkspaceFromHubKey(): Promise<Workspace | null> {
  try {
    const raw = localStorage.getItem(HUB_SYNC_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Workspace;
  } catch {
    return null;
  }
}

export const cloudSyncEngine: SyncEngine = {
  getStatus: () => currentStatus,

  initialize: (workspace: Workspace) => {
    if (!isPro()) {
      currentStatus = {
        mode: 'local-only',
        phase: 'synced',
        label: 'Local',
        message: 'Licence Free — mode local.',
        source: 'local',
        lastSyncAt: workspace.meta.updatedAt,
      };
      return;
    }

    const ts = getIndxSync()?.lastSyncedAt() || null;
    setStatus(buildStatus({
      label: 'Cloud',
      message: 'Sync cloud prêt.',
      source: 'cloud',
      lastSyncAt: ts ? new Date(ts).getTime() : undefined,
    }));
  },

  push: async (workspace: Workspace): Promise<SyncResult> => {
    if (!isPro()) {
      return { ok: false, status: { ...currentStatus, phase: 'error', message: 'Licence Pro requise pour le sync cloud.' } };
    }

    setStatus(buildStatus({ phase: 'syncing', message: 'Sauvegarde cloud en cours…' }));

    try {
      await writeWorkspaceToHubKey(workspace);

      const sync = getIndxSync();
      if (sync) {
        await sync.push();
        if (typeof sync.schedulePush === 'function') sync.schedulePush();
      }

      const status = buildStatus({
        phase: 'synced',
        message: 'Données sauvegardées sur le cloud.',
        lastSyncAt: Date.now(),
      });
      setStatus(status);

      return {
        ok: true,
        status,
        workspace,
      };
    } catch (err: any) {
      const status = buildStatus({
        phase: 'error',
        message: `Erreur de sauvegarde : ${err?.message || 'inconnue'}`,
      });
      setStatus(status);
      return { ok: false, status };
    }
  },

  pull: async (): Promise<SyncResult> => {
    if (!isPro()) {
      return { ok: false, status: { ...currentStatus, phase: 'error', message: 'Licence Pro requise pour le sync cloud.' } };
    }

    setStatus(buildStatus({ phase: 'syncing', message: 'Restauration cloud en cours…' }));

    try {
      const sync = getIndxSync();
      if (sync) {
        await sync.pull();
      }

      const remote = await readWorkspaceFromHubKey();
      const local = loadWorkspace();

      const resolution = resolveWorkspacePull(local, remote);
      if (resolution.conflict) {
        pendingConflict = {
          local,
          remote: remote!,
          detectedAt: Date.now(),
          localDevice: getDeviceLabel(),
          remoteDevice: remote?.meta.deviceLabel || 'Appareil distant',
        };
        const status = buildStatus({
          phase: 'conflict',
          message: 'Deux versions différentes ont été détectées. Choisissez celle à conserver.',
          lastSyncAt: currentStatus.lastSyncAt,
        });
        setStatus(status);
        return { ok: false, status, workspace: local };
      }

      const workspace = resolution.workspace;
      pendingConflict = null;

      saveWorkspace(workspace);

      const status = buildStatus({
        phase: 'synced',
        message: 'Données restaurées depuis le cloud.',
        lastSyncAt: Date.now(),
      });
      setStatus(status);

      return {
        ok: true,
        status,
        workspace,
      };
    } catch (err: any) {
      const status = buildStatus({
        phase: 'error',
        message: `Erreur de restauration : ${err?.message || 'inconnue'}`,
      });
      setStatus(status);

      const workspace = loadWorkspace();
      return {
        ok: false,
        status,
        workspace,
      };
    }
  },

  sync: async (workspace: Workspace): Promise<SyncResult> => {
    return cloudSyncEngine.push(workspace);
  },

  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
